import type { ElementType, ReactNode } from "react";

import { Tooltip } from "@alpacahq/ui";
import { clsx } from "@alpacahq/ui/old";

import { cn } from "~/v2/utils/cn";
import { getPrettyCash, getPrettyPercent } from "~/v2/utils/formatting";

interface AssetQuoteProps {
  symbol?: string;
  icon?: ReactNode;
  companyName?: string;
  price?: number;
  dailyChange?: number;
  dailyChangePercent?: number;

  // Icon toggles
  showNotTradableIcon?: boolean;
  showNoOvernightSupportIcon?: boolean;
  showOvernightHaltedIcon?: boolean;

  // Loading states
  isLoading?: boolean;
  isQuoteLoading?: boolean;

  // Interaction and state
  as?: ElementType;
  href?: string;
  onClick?: () => void;
  isHighlighted?: boolean;

  // Layout
  isCompact?: boolean;
}

export const AssetQuote = ({
  symbol,
  icon,
  companyName,
  price,
  dailyChange,
  dailyChangePercent,
  showNotTradableIcon = false,
  showNoOvernightSupportIcon = false,
  showOvernightHaltedIcon = false,
  isLoading,
  isQuoteLoading = false,
  as,
  href,
  onClick,
  isHighlighted = false,
  isCompact = false,
}: AssetQuoteProps) => {
  const Component = isLoading ? "div" : as || (href ? "a" : "button");
  // Show quote skeleton if either full loading or just quote loading
  const showQuoteSkeleton = isLoading || isQuoteLoading;
  const prettyDailyChange = getPrettyCash(dailyChange, {
    minimumFractionDigits: 2,
    withPrefix: true,
  });
  const prettyDailyChangePercent = getPrettyPercent(
    dailyChangePercent,
    false,
    true,
  );

  const prettyPrice = getPrettyCash(price, { minimumFractionDigits: 2 });
  const changeColor =
    dailyChange !== undefined && dailyChange >= 0
      ? "text-emerald-600"
      : "text-red-500";

  // Format zero change values explicitly to show "+$0.00" and "+0.00%"
  const displayDailyChange = dailyChange === 0 ? "+$0.00" : prettyDailyChange;
  const displayDailyChangePercent =
    dailyChangePercent === 0 ? "+0.00%" : prettyDailyChangePercent;

  const skeleton = (condition?: boolean) =>
    condition
      ? "animate-pulse bg-gray-200 text-transparent rounded select-none box-decoration-clone inline-block"
      : "";

  return (
    <Component
      href={!isLoading ? href : undefined}
      onClick={!isLoading ? onClick : undefined}
      type={Component === "button" ? "button" : undefined}
      className={clsx(
        "flex w-full flex-col justify-between rounded p-2 hover:bg-[#F8F8F8]",
        isCompact ? "h-21 gap-y-2" : "h-21 xs:h-16 gap-y-2 xs:gap-y-0",
        isHighlighted && "bg-[#F8F8F8]",
      )}
    >
      {/* Upper Row */}
      <div className="flex flex-row">
        <div
          className={clsx(
            "flex gap-x-3 gap-y-2",
            isCompact ? "flex-col" : "xs:flex-row flex-col",
          )}
        >
          <div className="flex flex-row gap-x-2">
            {isLoading ? (
              <div className="flex h-6 w-6 items-center">
                <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-gray-200" />
              </div>
            ) : (
              <div className="flex h-6 w-6 items-center">{icon}</div>
            )}
            <div
              className={clsx(
                "flex items-center font-semibold text-[#282828] text-sm",
                skeleton(isLoading),
              )}
            >
              {isLoading ? "Loading" : symbol}
            </div>
          </div>
          {(showQuoteSkeleton ||
            (dailyChange !== undefined &&
              dailyChangePercent !== undefined)) && (
            <div>
              <span
                className={clsx(
                  "text-sm",
                  changeColor,
                  skeleton(showQuoteSkeleton),
                )}
              >
                {showQuoteSkeleton ? "+$0.00" : displayDailyChange}
              </span>
              <span
                className={clsx(
                  "text-sm",
                  changeColor,
                  skeleton(showQuoteSkeleton),
                )}
              >
                ({showQuoteSkeleton ? "+0.00%" : displayDailyChangePercent})
              </span>
            </div>
          )}
        </div>

        <div
          className={clsx(
            "ml-auto flex gap-x-3 gap-y-2",
            isCompact
              ? "flex-col-reverse justify-end"
              : "xs:flex-row flex-col-reverse xs:justify-start justify-end",
          )}
        >
          <div className="ml-auto flex flex-row gap-x-3">
            {showNoOvernightSupportIcon && (
              <Tooltip content="Overnight trading is not supported for this asset.">
                <img
                  src="resources/icons/asset-search/ProhibitInset.svg"
                  alt="No Overnight Trading Support"
                  width={20}
                  height={20}
                  className={cn(!isCompact && "xs:mt-[-2px]")}
                />
              </Tooltip>
            )}
            {showOvernightHaltedIcon && (
              <Tooltip content="Overnight trading is temporarily suspended for this asset.">
                <img
                  src="resources/icons/asset-search/ExclamationMark.svg"
                  alt="Alpaca Halted"
                  width={20}
                  height={20}
                  className={cn(!isCompact && "xs:mt-[-2px]")}
                />
              </Tooltip>
            )}
            {showNotTradableIcon && (
              <Tooltip content="This asset cannot be traded on our platform at this time.">
                <img
                  src="resources/icons/asset-search/XCircle.svg"
                  alt="Not Tradable"
                  width={20}
                  height={20}
                  className={cn(!isCompact && "xs:mt-[-2px]")}
                />
              </Tooltip>
            )}
          </div>
          <span
            className={clsx(
              "font-medium text-[#282828] text-sm",
              !isCompact && "xs:ml-1",
              skeleton(showQuoteSkeleton),
            )}
          >
            {showQuoteSkeleton ? "$000.00" : (prettyPrice ?? "-")}
          </span>
        </div>
      </div>

      {/* Lower Row */}
      <div
        className={clsx(
          "min-w-0 truncate text-left font-normal text-[#696969] text-xs",
          skeleton(isLoading),
        )}
      >
        {isLoading ? "Loading" : companyName}
      </div>
    </Component>
  );
};
